import {Inject, Injectable} from "@angular/core";
import {ExchangeSocket} from "./exchange.socket";
import {BehaviorSubject, ReplaySubject, Subject} from "rxjs";
import {ExchangePriceControllerService, ExchangePriceWithRelations} from "@aparraga/ahoracrypto-api-sdk/sdk";
import {ProductService} from "../product/product.service";
import {IS_BROWSER} from "../utils/injectiontokens";
import { NavigationService } from "../navigation/navigation.service";

@Injectable({
  providedIn: 'root'
})
export class ExchangeService {

  protected exchangePrices: any = {};
  protected availableCryptos: string[] = [];
  public exchangePricesSubject$: Subject<any> = new ReplaySubject<any>();

  constructor(private exchangeSocket: ExchangeSocket,
              @Inject(IS_BROWSER) protected isBrowser: boolean,
              protected productService: ProductService,
              protected navigationService: NavigationService,
              protected exchangePriceControllerService: ExchangePriceControllerService) {

    if(this.isBrowser) {

      this.navigationService.queryParams$.subscribe(queryParams => {
        //check if queryParams contains crypto:
        if(queryParams != null && queryParams['cryptos'] != null) {
          //queryParams['availableCryptos'] is a comma separated list of cryptos, need to trim and upper each crypto
          this.availableCryptos = queryParams['cryptos'].split(',').map(crypto => crypto.trim().toUpperCase());
        }
      });

      //ensure that products are loaded
      this.exchangeSocket.on("exchanges", (exchangeRates) => {
        let exchangeRatesValidated = {};
        //validate each exchange rate before inserting it in the exchange rates collection
        for (const symbol in exchangeRates) {
          let valid = true; //by default
          for (const currency in exchangeRates[symbol]) {
            if (symbol == null || currency == null) {
              delete exchangeRates[symbol][currency];
            }
            else if (!exchangeRates[symbol][currency].hasOwnProperty('last24hVariation')) {
              exchangeRates[symbol][currency].last24hVariation = 0;
            }
          }
          if (exchangeRates[symbol] != null && Object.keys(exchangeRates[symbol]).length > 0) {
            exchangeRatesValidated[symbol] = exchangeRates[symbol];
          }
        }
        //how to know the length of a map?
        if(exchangeRatesValidated != null && Object.keys(exchangeRatesValidated).length > 0) {
          this.exchangePrices = exchangeRatesValidated;
          this.productService.refreshProducts().then(() => {
            //filter the exchange prices to only include the available cryptos but just if availableCryptos is not empty
            this.exchangePrices = this.filterExchangePricesByAvailableCryptos(this.exchangePrices);
            this.exchangePricesSubject$.next(this.exchangePrices);
          });
        }
      });
    }
    else {
      // SSR
      this.exchangePriceControllerService.exchangePriceControllerFindAll().subscribe(
        (exchangeRates) => {
          // in SSR, don't use the websocket, but the API
          // if len of exchangeRates > 0
          if(Object.keys(exchangeRates).length > 0) {
            this.exchangePrices = exchangeRates;
            this.productService.refreshProducts().then(() => {
              //filter the exchange prices to only include the available cryptos
              this.exchangePrices = this.filterExchangePricesByAvailableCryptos(this.exchangePrices);
              this.exchangePricesSubject$.next(this.exchangePrices);
            });
          }
        });
    }

  }

  public calculateExchange(from: string, fromAmount: number, to: string): number {
    let exhangeRate = 0;

    let exchangePrice = this.getExchangePrice(to, from);
    if(exchangePrice != null) {
      exhangeRate = 1 / exchangePrice.price;
    }
    else {
      exchangePrice = this.getExchangePrice(from, to);
      if(exchangePrice != null) {
        exhangeRate = exchangePrice.price;
      }
    }
    return exhangeRate * fromAmount;

  }

  public refreshExchangePrices() {
    this.exchangePriceControllerService.exchangePriceControllerFindAll().subscribe( (exchangeRates) => {
      this.exchangePrices = exchangeRates;
    });
  }

  public getExchangePricesByQuoteCurrency(quoteCurrency: string): any {
    let returnValue = [];
    let quoteStableCoin = this.getStableCoin(quoteCurrency);
    let products = this.productService.getProducts();
    if(this.exchangePrices != null) {
      Object.keys(this.exchangePrices).forEach(async baseCurrency => {
        if(!!products[baseCurrency] && !!this.exchangePrices[baseCurrency][quoteStableCoin]) {
          returnValue.push(this.exchangePrices[baseCurrency][quoteStableCoin]);
        }
      });
    }
    return returnValue;
  }

  public getExchangePricesOrderByHottest(quoteCurrency, limit = null, offset = null) {

    let products = this.productService.getProducts();
    let returnValue = this.getExchangePricesByQuoteCurrency(quoteCurrency).filter(exchangePrice => products[exchangePrice.baseCurrency].listable);

    returnValue.sort((a, b) => {
      if (a.last24hNumberOfTrades > b.last24hNumberOfTrades) {
        return -1;
      }
      if (a.last24hNumberOfTrades < b.last24hNumberOfTrades) {
        return 1;
      }
      return 0;
    });

    if(offset == null) {
      offset = 0;
    }
    if(limit != null) {
      returnValue = returnValue.slice(offset, offset + limit);
    }
    return returnValue;

  }

  public getExchangePricesOrderByVolume(quoteCurrency, limit = null, offset = null) {

    let products = this.productService.getProducts();
    let returnValue = this.getExchangePricesByQuoteCurrency(quoteCurrency).filter(exchangePrice => products[exchangePrice.baseCurrency].listable);

    returnValue.sort((a, b) => {
      if (a.last24hQuoteAssetVolume > b.last24hQuoteAssetVolume) {
        return -1;
      }
      if (a.last24hQuoteAssetVolume < b.last24hQuoteAssetVolume) {
        return 1;
      }
      return 0;
    });

    if(offset == null) {
      offset = 0;
    }
    if(limit != null) {
      returnValue = returnValue.slice(offset, offset + limit);
    }
    return returnValue;

  }

  public getExchangePricesOrderByMarketCap(quoteCurrency, limit = null, offset = null) {

    let products = this.productService.getProducts();
    let returnValue = this.getExchangePricesByQuoteCurrency(quoteCurrency).filter(exchangePrice => products[exchangePrice.baseCurrency].listable);

    returnValue.sort((a, b) => {
      if (a.cmcRank == null && b.cmcRank == null) {
        //if both cmcrank are null, then sort by volume
        if (a.last24hQuoteAssetVolume > b.last24hQuoteAssetVolume) {
          return -1;
        }
        if (a.last24hQuoteAssetVolume < b.last24hQuoteAssetVolume) {
          return 1;
        }
      }
      else if (a.cmcRank == null || a.cmcRank > b.cmcRank) {
        return 1;
      }
      else if (b.cmcRank == null || a.cmcRank < b.cmcRank) {
        return -1;
      }
      return 0;
    });

    if(offset == null) {
      offset = 0;
    }
    if(limit != null) {
      returnValue = returnValue.slice(offset, offset + limit);
    }
    return returnValue;

  }

  public getExchangePricesOrderByWinners(quoteCurrency, limit = null, offset = null) {

    let products = this.productService.getProducts();
    let returnValue = this.getExchangePricesByQuoteCurrency(quoteCurrency).filter(exchangePrice => products[exchangePrice.baseCurrency].listable);

    returnValue.sort((a, b) => {
      if (a.last24hVariation > b.last24hVariation) {
        return -1;
      }
      if (a.last24hVariation < b.last24hVariation) {
        return 1;
      }
      return 0;
    });

    if(offset == null) {
      offset = 0;
    }
    if(limit != null) {
      returnValue = returnValue.slice(offset, offset + limit);
    }
    return returnValue;

  }

  public getExchangePricesOrderByLosers(quoteCurrency, limit = null, offset = null) {

    let products = this.productService.getProducts();
    let returnValue = this.getExchangePricesByQuoteCurrency(quoteCurrency).filter(exchangePrice => products[exchangePrice.baseCurrency].listable);

    returnValue.sort((a, b) => {
      if (a.last24hVariation < b.last24hVariation) {
        return -1;
      }
      if (a.last24hVariation > b.last24hVariation) {
        return 1;
      }
      return 0;
    });

    if(offset == null) {
      offset = 0;
    }
    if(limit != null) {
      returnValue = returnValue.slice(offset, offset + limit);
    }
    return returnValue;

  }

  public getExchangePricesOrderByNewers(quoteCurrency, limit = 3, offset = null) {

    let products = this.productService.getProducts();
    let returnValue = this.getExchangePricesByQuoteCurrency(quoteCurrency).filter(exchangePrice => products[exchangePrice.baseCurrency].listable);

    returnValue.sort((a, b) => {
      if(!!products[a.baseCurrency] && !!products[b.baseCurrency]) {
        let crypto1 = products[a.baseCurrency];
        let crypto2 = products[b.baseCurrency];
        if (crypto1 != null && crypto2 != null) {
          if (crypto1.created > crypto2.created) {
            return -1;
          }
          if (crypto1.created < crypto2.created) {
            return 1;
          }
        }
      }
      return 0;
    });

    if(offset == null) {
      offset = 0;
    }
    if(limit != null) {
      returnValue = returnValue.slice(offset, offset + limit);
    }
    return returnValue;

  }


  public getExchangePrice(baseCurrency: string, quoteCurrency: string): ExchangePriceWithRelations {

    let baseStableCoin = this.getStableCoin(baseCurrency);
    let quoteStableCoin = this.getStableCoin(quoteCurrency);

    let baseSymbol = null;
    let quoteSymbol = null;

    if(!!this.exchangePrices[baseCurrency]) {
      baseSymbol = baseCurrency;
    }
    else if(!!this.exchangePrices[baseStableCoin]) {
      baseSymbol = baseStableCoin;
    }

    if(baseSymbol != null) {
      if (!!this.exchangePrices[baseSymbol][quoteCurrency]) {
        quoteSymbol = quoteCurrency;
      } else if (!!this.exchangePrices[baseSymbol][quoteStableCoin]) {
        quoteSymbol = quoteStableCoin;
      }
    }

    if(baseSymbol != null && quoteSymbol != null) {
      return this.exchangePrices[baseSymbol][quoteSymbol];
    }
    else {
      return null;
    }

  }


  public getStableCoin(symbol: string) {
    let returnValue: string;
    if(symbol == 'USD') {
      returnValue = 'USDC';
    }
    else {
      returnValue = symbol;
    }
    return returnValue;
  }

  public getExchangePrices(): any {
    return this.exchangePrices;
  }


  /**
   * Filter exchange prices by available cryptos
   * @private
   */
  private filterExchangePricesByAvailableCryptos(exchangePrices: any): any {
    if(this.availableCryptos.length === 0) {
      return exchangePrices;
    }
    
    const filteredExchangePrices = {};
    Object.keys(exchangePrices).forEach(symbol => {
      if (this.availableCryptos.includes(symbol.toUpperCase())) {
        filteredExchangePrices[symbol] = exchangePrices[symbol];
      }
    });
    return filteredExchangePrices;
  }


}
